import {
  Component,
  OnInit,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import { Subject } from 'rxjs';

import {
  UseCasePreviewResponse,
  UseCaseProvisionFailure,
} from 'src/app/core/models/use-case.models';
import { TabComponent } from 'src/app/shared/utils/tab-manager';
import { UseCasePlanSummaryComponent } from '../../use-case-plan-summary/use-case-plan-summary.component';
import { TranslatePipe } from '@ngx-translate/core';

type PreviewState = 'idle' | 'loading' | 'error';

/**
 * Step 5 (§12.1): renders the §9.2 plan as objects-to-create + quota + errors.
 * Its FormGroup carries a single `preview_valid` control (requiredTrue) so the
 * tab manager keeps the step - and the wizard's Create button - invalid until
 * preview returns valid:true. The wizard drives the API call and pushes the
 * result in via setPreview().
 */
@Component({
  selector: 'app-use-case-preview-step',
  templateUrl: './use-case-preview-step.component.html',
  styleUrls: ['./use-case-preview-step.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [UseCasePlanSummaryComponent, TranslatePipe],
})
export class UseCasePreviewStepComponent implements OnInit, TabComponent {
  formGroup: FormGroup = new FormGroup({
    preview_valid: new FormControl<boolean>(false, Validators.requiredTrue),
  });
  formGroupChange = new Subject<FormGroup>();

  readonly state = signal<PreviewState>('idle');
  readonly preview = signal<UseCasePreviewResponse | null>(null);
  // Set from the provision response's §9.4 failure body when a provision fails.
  readonly provisionFailure = signal<UseCaseProvisionFailure | null>(null);

  ngOnInit(): void {
    this.formGroupChange.next(this.formGroup);
  }

  /** Mark the step as loading its plan; clears any prior result. */
  setLoading(): void {
    this.state.set('loading');
    this.preview.set(null);
    this.provisionFailure.set(null);
    this.setValid(false);
  }

  /** Mark the preview call as failed (network/transport, not a §9.3 error). */
  setError(): void {
    this.state.set('error');
    this.setValid(false);
  }

  /**
   * Push a preview response and gate the Create button on its validity (§9.2).
   *
   * @param response the preview envelope
   * @return void
   */
  setPreview(response: UseCasePreviewResponse): void {
    this.state.set('idle');
    this.provisionFailure.set(null);
    this.preview.set(response);
    this.setValid(response.valid);
  }

  /**
   * Surface a §9.4 provision failure inline; keep the step invalid so the user
   * can retry (no client-side cleanup - the backend owns compensation).
   *
   * @param failure the §9.4 failure body
   * @return void
   */
  setProvisionFailure(failure: UseCaseProvisionFailure): void {
    this.state.set('idle');
    this.provisionFailure.set(failure);
    this.setValid(false);
  }

  /**
   * Set the gate control's value and refresh validity.
   *
   * @param valid whether preview is valid
   * @return void
   */
  private setValid(valid: boolean): void {
    this.formGroup.controls['preview_valid'].setValue(valid);
    this.formGroup.controls['preview_valid'].markAsDirty();
  }
}
